import { useContext, useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  empty,
  errorResponse,
  GetPosition,
  isArray,
  isObject,
  isString,
  isUndefined,
  prepareResponseData,
  reIndex,
  toNormalCase,
} from "../../Utilities/utils";
import { AuthContext } from "../Root/ProtectedRoute";
import { toUpper } from "lodash";
import * as Yup from "yup";

// css
import "../students/Students.css";
import "./Assessment.css";

// api
import studentApi from "../../api/Student";
import classApi from "../../api/Classes";
import termApi from "../../api/Terms";
import assessmentApi from "../../api/Assessment";
import sessionApi from "../../api/Session";

//components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import AppWrapper from "../../components/appWrapper/AppWrapper";
import { Toast } from "primereact/toast";
import FullPageLoader from "../../components/loader/FullPageLoader";
import { Button } from "primereact/button";
import { FaExclamationTriangle, FaPrint } from "react-icons/fa";
import colors from "../../config/colors";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import Barchart from "../../components/chart/barchart/Barchart";
import ItemLoader from "../../components/loader/ItemLoader";
import { Form, Formik } from "formik";
import SelectField from "../../components/form/SelectField";
import EmptyListResponse from "../../components/emptyDiv/EmptyListResponse";
import TitleValue from "../../components/item/TitleValue";

const required = "This field is required!";
const validationSchema = Yup.object().shape({
  student_id: Yup.string().required(required),
});
const DisplayReportSheet = ({ ...props }) => {
  const printRef = useRef();
  const { user } = useContext(AuthContext);
  const location = useLocation();

  const [initialValues, setInitialValues] = useState({
    student_id: "",
  });
  const navigate = useNavigate();
  //ref
  const toastTR = useRef(null);
  // states
  const [studentsData, setStudentsData] = useState([]);
  const [classData, setClassData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [studentAssessment, setStudentAssessment] = useState([]);
  const [reIndexedSubjects, setReIndexedSubjects] = useState({});
  const [activeDetails, setActiveDetails] = useState({});
  const [termData, setTermData] = useState({});
  const [sessionData, setSessionData] = useState({});
  const [studentReport, setStudentReport] = useState({});
  const [hasPaid, setHasPaid] = useState(false);
  const [classTeacherName, setClassTeacherName] = useState("");
  const [ratingIndices, setRatingIndices] = useState([]);
  const [updateKey, setUpdateKey] = useState("");
  const [selectedStudentData, setSelectedStudentData] = useState({});
  const [activationModalOpen, setActivationModalOpen] = useState(false);
  const [chartData, setChartData] = useState({
    labels: studentAssessment.map((data) => {
      const subjectCode =
        !empty(data) && !empty(data.subjectCode) ? data.subjectCode : "";
      return subjectCode;
    }),
    datasets: [
      {
        label: "Student Performance",
        data: studentAssessment.map((data) => {
          const sum = !empty(data) && !empty(data.total) ? data.total : 0;
          return sum;
        }),
        backgroundColor: colors.charcoal,
        borderRadius: 12,
      },
      {
        label: "Class Average",
        data: studentAssessment.map((data) => {
          const sum = !empty(data) && !empty(data.total) ? data.total : 0;
          return sum;
        }),
        backgroundColor: colors.charcoal,
        borderRadius: 12,
      },
    ],
  });
  const { studentId: _studentId, termId, sessionId, classId } = useParams();
  const [studentId, setStudentId] = useState(_studentId);

  useEffect(() => {
    if (!studentId || !termId || !sessionId || !classId) {
      navigate("/404");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // fetch student
    try {
      if (!empty(studentId) && !empty(sessionId) && !empty(termId)) {
        getStudents();
      } else {
        navigate("/404");
      }
    } catch (error) {
      responseDialog(
        "error",
        "Something went wrong",
        "Failed to load student. Please try again later.",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  useEffect(() => {
    getTermDetails();
    getSessionDetails();
    getSubjectsInClass();
    getRatingIndices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPaid]);

  useEffect(() => {
    assessmentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudentData, reIndexedSubjects, selectedStudentData, studentId]);

  useEffect(() => {}, [updateKey]);

  // function to get all student
  const getStudents = async (returnValue = false, getReport = true) => {
    try {
      if (!isLoading) setIsLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      let response = await studentApi.getStudentsInClass(
        schoolId,
        null,
        null,
        null,
        classId,
        "assessment",
      );
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return setStudentsData([]);
      } else {
        const returnData = isArray(response_data?.response)
          ? response_data.response
          : [];
        if (returnValue) {
          return returnData;
        } else {
          // get current student details
          let studentData = {};
          for (let i = 0; i < returnData.length; i++) {
            if (returnData?.[i]?._id === studentId) {
              studentData = returnData[i];
              const hasPaid = studentData?.paid === "Yes" ? true : false;
              setHasPaid(hasPaid);
              setInitialValues((prev) => ({
                ...prev,
                student_id: returnData[i]?._id,
              }));
              setStudentId(returnData[i]?._id || "");
              setSelectedStudentData(returnData[i]);
              break;
            }
          }
          if (getReport) {
            getStudentReportSheet(studentData, false);
          }
          return setStudentsData(returnData);
        }
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  const getClassDetails = async () => {
    try {
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await classApi.getSingleClass(classId, schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return setClassData({});
      } else {
        const data =
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : {};
        const classTeacherName =
          !empty(data) && !empty(data.classTeacher) ? data.classTeacher : "";
        setClassData(data);
        return setClassTeacherName(classTeacherName);
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    }
  };

  const getTermDetails = async () => {
    try {
      const response = await termApi.getSingleTerm(termId);
      const response_data = prepareResponseData(response);
      if (
        empty(response_data) ||
        empty(response_data.success) ||
        !response_data.success
      ) {
        return setTermData({});
      } else {
        return setTermData(response_data.response);
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    }
  };

  const getRatingIndices = async () => {
    try {
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await assessmentApi.getRatings(schoolId);
      const response_data = prepareResponseData(response);
      if (
        empty(response_data) ||
        empty(response_data.success) ||
        !response_data.success
      ) {
        return setRatingIndices({});
      } else {
        return setRatingIndices(response_data.response);
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    }
  };

  const getSubjectsInClass = async () => {
    try {
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await classApi.getSubjectsInClass(
        classId,
        schoolId,
        1000,
      );
      const response_data = prepareResponseData(response);
      if (
        empty(response_data) ||
        empty(response_data.success) ||
        !response_data.success
      ) {
        return setReIndexedSubjects({});
      } else {
        const classSubjects = response_data.response;
        const reIndexedSubjects = reIndex(classSubjects, "_id");
        return setReIndexedSubjects(reIndexedSubjects);
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    }
  };

  const getSessionDetails = async () => {
    try {
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await sessionApi.getSession(sessionId, schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return setSessionData({});
      } else {
        return setSessionData(response_data.response);
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    }
  };

  //alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  /**
   * Get assessment data
   */
  const assessmentData = () => {
    try {
      const studentAssessmentData =
        !empty(selectedStudentData) && !empty(selectedStudentData.assessment)
          ? selectedStudentData.assessment
          : [];
      const studentReport =
        !empty(selectedStudentData) && !empty(selectedStudentData.reports)
          ? selectedStudentData.reports
          : [];
      const classReports =
        !empty(classData) && !empty(classData.reports) ? classData.reports : [];
      let classReport = {};
      for (let i = 0; i < classReports.length; i++) {
        const selectedStudentData = !empty(classReports[i])
          ? classReports[i]
          : {};
        const reportSessionId = !empty(selectedStudentData.sessionId)
          ? selectedStudentData.sessionId
          : "";
        const reportTermId = !empty(selectedStudentData.termId)
          ? selectedStudentData.termId
          : "";
        if (reportSessionId === sessionId && reportTermId === termId) {
          classReport = selectedStudentData;
          break;
        }
      }
      const selectedSubjectIds =
        !empty(classReport) && !empty(classReport.subjectsToAssess)
          ? classReport.subjectsToAssess
          : [];
      const currentStudentAssessmentData = studentAssessmentData.filter(
        // eslint-disable-next-line array-callback-return
        (item) => {
          const itemSessionId =
            !empty(item) && !empty(item.sessionId) ? item.sessionId : "";
          const itemTermId =
            !empty(item) && !empty(item.termId) ? item.termId : "";
          const itemSubjectId =
            !empty(item) && !empty(item.subjectId) ? item.subjectId : "";
          if (
            itemSessionId === sessionId &&
            itemTermId === termId &&
            selectedSubjectIds.includes(itemSubjectId)
          ) {
            const subjectId =
              !empty(item) && !empty(item.subjectId) ? item.subjectId : "";
            item.subjectCode =
              !empty(reIndexedSubjects) &&
              !empty(reIndexedSubjects[subjectId]) &&
              !empty(reIndexedSubjects[subjectId].code)
                ? reIndexedSubjects[subjectId].code
                : "";
            return (item.subjectTitle =
              !empty(reIndexedSubjects) &&
              !empty(reIndexedSubjects[subjectId]) &&
              !empty(reIndexedSubjects[subjectId].title)
                ? reIndexedSubjects[subjectId].title
                : "");
          }
        },
      );

      let currentReport = {};
      for (let i = 0; i < studentReport.length; i++) {
        const itemSessionId =
          !empty(studentReport[i]) && !empty(studentReport[i].sessionId)
            ? studentReport[i].sessionId
            : "";
        const itemTermId =
          !empty(studentReport[i]) && studentReport[i].termId
            ? studentReport[i].termId
            : "";
        if (itemSessionId === sessionId && itemTermId === termId) {
          currentReport = !empty(studentReport[i]) ? studentReport[i] : {};

          selectedStudentData.psychomotor =
            !empty(studentReport[i]) && !empty(studentReport[i].psychomotor)
              ? studentReport[i].psychomotor
              : {};
          selectedStudentData.affectiveDomain =
            !empty(studentReport[i]) && !empty(studentReport[i].affectiveDomain)
              ? studentReport[i].affectiveDomain
              : {};
          break;
        }
      }
      setStudentReport(currentReport);
      setStudentAssessment(currentStudentAssessmentData);
      setActiveDetails(selectedStudentData);
      // set chart data
      setChartData({
        labels: currentStudentAssessmentData.map((data) => {
          const subjectCode =
            !empty(data) && !empty(data.subjectCode) ? data.subjectCode : "";
          return subjectCode;
        }),
        datasets: [
          {
            label: "Subject Performance",
            data: currentStudentAssessmentData.map((data) => {
              const sum = !empty(data) && !empty(data.total) ? data.total : 0;
              return sum;
            }),
            backgroundColor: colors.primary,
            borderRadius: 12,
          },
          {
            label: `Class Average`,
            data: currentStudentAssessmentData.map((data) => {
              const average =
                !empty(data) && !empty(data.classAverage)
                  ? data.classAverage
                  : 0;
              return average;
            }),
            backgroundColor: colors.orange,
            borderRadius: 12,
          },
        ],
      });

      const classTeacherName =
        !empty(currentReport) && !empty(currentReport.classTeacherName)
          ? currentReport.classTeacherName
          : "";
      if (empty(classTeacherName)) {
        return getClassDetails();
      }
      return setClassTeacherName(classTeacherName);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  // function to show result sheet
  const getStudentReportSheet = (data) => {
    const hasPaid =
      !empty(data) && !empty(data.paid) && data.paid === "Yes" ? true : false;
    setHasPaid(hasPaid);
    assessmentData(data);
    setUpdateKey(new Date());
  };

  // hide activation modal
  const hideActivationModal = () => {
    setActivationModalOpen(false);
  };

  //function to activate student on payment
  const activateStudent = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      if (activationModalOpen) setActivationModalOpen(false);
      if (!isActionLoading) setIsActionLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const studentId =
        !empty(selectedStudentData) && !empty(selectedStudentData._id)
          ? selectedStudentData._id
          : "";
      const response = await studentApi.activateStudent([studentId], schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        const error_response =
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : `Failed to activate student`;
        return responseDialog("error", "Error Alert", error_response);
      }

      // get current student details
      getStudents(false, true);
      return responseDialog(
        "success",
        "Success",
        `Activated student successfully!`,
      );
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsActionLoading(false);
      setIsLoading(false);
    }
  };

  const activateStudentDialogFooter = (
    <div
      style={{
        marginTop: 20,
      }}
    >
      <Button
        label="No"
        icon="pi pi-times"
        style={{
          backgroundColor: "transparent",
          color: "#e65061",
          borderColor: "#e65061",
          borderWidth: 1,
        }}
        onClick={hideActivationModal}
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        style={{
          backgroundColor: "transparent",
          color: "#389d17",
          borderColor: "#389d17",
          borderWidth: 1,
        }}
        onClick={() => {
          activateStudent();
        }}
      />
    </div>
  );
  const printReport = () => {
    window.print();
  };

  // get student data
  const getCurrentStudentData = (values) => {
    try {
      if (!isLoading) setIsLoading(true);
      if (values) {
        const studentId = values?.student_id || "";
        setStudentId(studentId);

        // get current student details
        let studentData = {};
        for (let i = 0; i < studentsData.length; i++) {
          if (studentsData?.[i]?._id === studentId) {
            studentData = studentsData[i];
            const hasPaid = studentData?.paid === "Yes" ? true : false;
            setHasPaid(hasPaid);
            setInitialValues((prev) => ({
              ...prev,
              student_id: studentsData[i]?._id,
            }));
            setSelectedStudentData(studentsData[i]);
            break;
          }
        }
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AppWrapper {...props}>
        <main>
          <div className="tableCard">
            {/*  header start */}
            <MainHeader title="Result Sheet Display" />
            {/* end of header */}

            <div className="mt-10 mb-40">
              <div className="flex report-academic-details">
                <TitleValue
                  value={sessionData?.title || ""}
                  title="Selected Session:"
                />
                <TitleValue
                  value={termData?.title || ""}
                  title="Selected Term:"
                  mr="mr-30"
                />
                {!location?.state?.graduated && (
                  <TitleValue
                    value={classData?.title}
                    title="Selected Class:"
                    mr="mr-30"
                  />
                )}
                {location?.state?.graduated && (
                  <TitleValue
                    value="Graduated"
                    title="Student's Status:"
                    mr="mr-30"
                  />
                )}
              </div>
              {!location?.state?.graduated && (
                <Formik
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={getCurrentStudentData}
                >
                  {({ values, handleChange }) => (
                    <Form style={{ width: "100%" }}>
                      <div className="app_input_group">
                        <SelectField
                          labelTitle="Select Students"
                          required={true}
                          placeholder="Select Student"
                          name="student_id"
                          options={studentsData}
                          height={50}
                          valueKey="id"
                          display="fullName"
                          containerWidth={350}
                          selectedOption={values.student_id}
                          handleChangeFunc={handleChange}
                        />
                        <ButtonIcon
                          height={45}
                          marginTop={5}
                          color="#ffffff"
                          backgroundColor="#633ccd"
                          width={130}
                          borderColor="#633ccd"
                          buttonText="Submit"
                          type="submit"
                        />
                      </div>
                    </Form>
                  )}
                </Formik>
              )}
            </div>
            <div className="display-report" key={`${studentId}-${updateKey}`}>
              <div className="report-sheet-display">
                {!isLoading ? (
                  <>
                    {hasPaid ? (
                      <>
                        <div className="report-body" ref={printRef}>
                          {/* report header */}
                          <div className="report-header">
                            <div style={{ width: 100, height: 100 }}>
                              <img
                                src={
                                  !empty(user) && !empty(user.avatar)
                                    ? user.avatar
                                    : ""
                                }
                                alt="non"
                                style={{ width: "100%", height: "100%" }}
                              />
                            </div>
                            <div>
                              <p className="report-title">
                                {!empty(user) && !empty(user.name)
                                  ? user.name
                                  : "---"}
                              </p>
                              <p className="report-subtitle">
                                {!empty(user) && !empty(user.address)
                                  ? user.address
                                  : "---"}
                              </p>
                              <p className="report-subtitle fs-14">
                                {`TEL.: ${
                                  !empty(user) && !empty(user.phoneNumber1)
                                    ? user.phoneNumber1
                                    : ""
                                }${
                                  !empty(user) && !empty(user.phoneNumber2)
                                    ? ", " + user.phoneNumber2
                                    : ""
                                }${
                                  !empty(user) && !empty(user.ownerPhone)
                                    ? ", " + user.ownerPhone
                                    : ""
                                }`}
                              </p>
                              <p className="report-subtitle fs-14 mb-10">
                                {`Email: ${
                                  !empty(user) && !empty(user.email)
                                    ? user.email
                                    : "---"
                                }`}
                              </p>
                            </div>
                            <div style={{ width: 100, height: 100 }}>
                              {!empty(activeDetails) &&
                              !empty(activeDetails.avatar) ? (
                                <img
                                  src={activeDetails.avatar}
                                  alt="student"
                                  style={{ width: "100%", height: "100%" }}
                                />
                              ) : (
                                <img
                                  src={
                                    !empty(user) && !empty(user.avatar)
                                      ? user.avatar
                                      : ""
                                  }
                                  alt="non"
                                  style={{ width: "100%", height: "100%" }}
                                />
                              )}
                            </div>
                          </div>
                          {/* end of report header */}

                          {/* report top section */}
                          <div className="report-details-header">
                            <p className="report-term-title">
                              {!empty(sessionData) && !empty(sessionData.title)
                                ? sessionData.title
                                : ""}{" "}
                              {!empty(termData) &&
                              !empty(termData.title) &&
                              isString(termData.title)
                                ? termData.title.toUpperCase()
                                : ""}{" "}
                              STUDENT'S PERFORMANCE SHEET
                            </p>
                            <div className="report-detail-group">
                              <p className="report-detail-group-item">
                                <span className="report-detail-group-title">
                                  NAME:
                                </span>
                                <span className="report-detail-group-value">
                                  <NavLink
                                    to={`/student/${studentId}/profile`}
                                    style={{
                                      textDecoration: "none",
                                      color: "#000000",
                                    }}
                                  >
                                    {!empty(activeDetails) &&
                                    !empty(activeDetails.fullName)
                                      ? toUpper(activeDetails.fullName)
                                      : "---"}
                                  </NavLink>
                                </span>
                              </p>
                              <p className="report-detail-group-item">
                                <span className="report-detail-group-title">
                                  GENDER:
                                </span>
                                <span className="report-detail-group-value">
                                  {!empty(activeDetails) &&
                                  !empty(activeDetails.gender)
                                    ? toUpper(activeDetails.gender)
                                    : "---"}
                                </span>
                              </p>
                              <p className="report-detail-group-item">
                                <span className="report-detail-group-title">
                                  CLASS:
                                </span>
                                <span className="report-detail-group-value">
                                  {!empty(classData) && !empty(classData.title)
                                    ? toUpper(classData.title)
                                    : "---"}
                                </span>
                              </p>
                            </div>
                            <div className="report-detail-group">
                              <p className="report-detail-group-item">
                                <span className="report-detail-group-title">
                                  ADMISSION NO.:
                                </span>
                                <span className="report-detail-group-value">
                                  {!empty(activeDetails) &&
                                  !empty(activeDetails.regNo)
                                    ? toUpper(activeDetails.regNo)
                                    : "---"}
                                </span>
                              </p>
                              <p className="report-detail-group-item">
                                <span className="report-detail-group-title">
                                  ROLL NO.:
                                </span>
                                <span className="report-detail-group-value">
                                  {!empty(activeDetails) &&
                                  !empty(activeDetails.rollNumber)
                                    ? toUpper(activeDetails.rollNumber)
                                    : "---"}
                                </span>
                              </p>
                            </div>

                            <div className="report-rating-box">
                              <table className="grade-rating-table">
                                <thead>
                                  <tr>
                                    <th>GRADE</th>
                                    <th>A</th>
                                    <th>B</th>
                                    <th>C</th>
                                    <th>D</th>
                                    <th>E</th>
                                    <th>F</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <th>NO</th>
                                    <td>
                                      {!empty(studentReport) &&
                                      !empty(studentReport.noOfAs)
                                        ? toUpper(studentReport.noOfAs)
                                        : 0}
                                    </td>
                                    <td>
                                      {!empty(studentReport) &&
                                      !empty(studentReport.noOfBs)
                                        ? toUpper(studentReport.noOfBs)
                                        : 0}
                                    </td>
                                    <td>
                                      {!empty(studentReport) &&
                                      !empty(studentReport.noOfCs)
                                        ? toUpper(studentReport.noOfCs)
                                        : 0}
                                    </td>
                                    <td>
                                      {!empty(studentReport) &&
                                      !empty(studentReport.noOfDs)
                                        ? toUpper(studentReport.noOfDs)
                                        : 0}
                                    </td>
                                    <td>
                                      {!empty(studentReport) &&
                                      !empty(studentReport.noOfEs)
                                        ? toUpper(studentReport.noOfEs)
                                        : 0}
                                    </td>
                                    <td>
                                      {!empty(studentReport) &&
                                      !empty(studentReport.noOfFs)
                                        ? toUpper(studentReport.noOfFs)
                                        : 0}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th colSpan={6}>
                                      NUMBER OF SUBJECTS OFFERED
                                    </th>
                                    <td>
                                      {studentAssessment?.length || 0} out of
                                      {" " +
                                        studentReport?.noOfSubjectsOffered || 0}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>

                              <table className="score-rating-table">
                                <tbody>
                                  <tr>
                                    <th>AVERAGE</th>
                                    <td>
                                      {!empty(studentReport) &&
                                      !empty(studentReport.average)
                                        ? studentReport.average
                                        : "---"}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th>GRADE</th>
                                    <td>
                                      {!empty(studentReport) &&
                                      !empty(studentReport.grade) &&
                                      !empty(studentReport.remark)
                                        ? studentReport.grade +
                                          " - " +
                                          toUpper(studentReport.remark)
                                        : "---"}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th>POSITION</th>
                                    <td>
                                        {classData?.isStream === true ? toUpper(
                                              studentReport?.streamPosition || 'N/A') : toUpper(
                                              studentReport.position || 'N/A'
                                            )}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>

                              <table className="score-rating-table">
                                <tbody>
                                  <tr>
                                    <td>Number of Students in class</td>
                                    <td>
                                      {!empty(selectedStudentData) &&
                                      !empty(
                                        selectedStudentData.numberOfStudents,
                                      )
                                        ? selectedStudentData.numberOfStudents
                                        : "---"}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Marks Obtained</td>
                                    <td>
                                      {!empty(studentReport) &&
                                      !empty(studentReport.cumulativeScore)
                                        ? studentReport.cumulativeScore
                                        : "---"}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Marks Obtainable</td>
                                    <td>
                                      {!empty(studentReport) &&
                                      !empty(studentReport.noOfSubjectsOffered)
                                        ? studentReport.noOfSubjectsOffered *
                                          100
                                        : "---"}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                          {/* end of report top section */}

                          <div className="report-containter">
                            <table className="report-sheet-area">
                              <thead>
                                <tr>
                                  <th rowSpan={3}>COGNITIVE DOMAIN</th>
                                  <th rowSpan={4}>
                                    <span className="report-title-rotate">
                                      CODE
                                    </span>
                                  </th>
                                  <th rowSpan={2} colSpan={user?.noOfCAs || 2}>
                                    CA
                                  </th>
                                  <th rowSpan={3}>
                                    <span className="report-title-rotate">
                                      EXAM
                                    </span>
                                  </th>
                                  <th rowSpan={3}>
                                    <span className="report-title-rotate">
                                      TOTAL
                                    </span>
                                  </th>
                                  <th rowSpan={4}>
                                    <span className="report-title-rotate">
                                      GRADE
                                    </span>
                                  </th>
                                  <th rowSpan={4}>
                                    <span className="report-title-rotate">
                                      POSITION
                                    </span>
                                  </th>
                                  <th rowSpan={4}>
                                    <span className="report-title-rotate">
                                      REMARKS
                                    </span>
                                  </th>
                                  <th rowSpan={4}>
                                    <span className="report-title-rotate">
                                      CLASS MIN.
                                    </span>
                                  </th>
                                  <th rowSpan={4}>
                                    <span className="report-title-rotate">
                                      CLASS MAX.
                                    </span>
                                  </th>
                                  <th rowSpan={4}>
                                    <span className="report-title-rotate">
                                      CLASS AVG.
                                    </span>
                                  </th>
                                </tr>
                                <tr></tr>
                                <tr>
                                  {
                                    Array.from({ length: user?.noOfCAs || 2 }, (_, idx) => (
                                      <th key={idx}>{GetPosition(idx + 1)}</th>
                                    ))
                                  }
                                </tr>
                                <tr>
                                  <th>SUBJECTS</th>
                                  {
                                    Array.from({ length: user?.noOfCAs || 2 }, (_, idx) => (
                                      <th key={idx}>{user?.assessmentMaxScores?.[`ca${idx + 1}_max`]}</th>
                                    ))
                                  }
                                  <th>{user?.assessmentMaxScores?.exam_max || 0}</th>
                                  <th>100</th>
                                </tr>
                              </thead>
                              <tbody>
                                {!empty(studentAssessment) ? (
                                  studentAssessment.map((data) => {
                                    return (
                                      <tr
                                        key={
                                          !empty(data) && !empty(data.subjectId)
                                            ? data.subjectId
                                            : ""
                                        }
                                      >
                                        <td>
                                          {!empty(data) &&
                                          !empty(data.subjectTitle)
                                            ? toUpper(data.subjectTitle)
                                            : ""}
                                        </td>
                                        <td>
                                          {!empty(data) &&
                                          !empty(data.subjectCode)
                                            ? data.subjectCode
                                            : 0}
                                        </td>
                                          {
                                            Array.from({ length: user?.noOfCAs || 2 }, (_, idx) => (
                                              <td key={idx}>
                                                { data?.[`ca${idx + 1}`] || 0 }
                                              </td>
                                            ))
                                          }
                                        <td>
                                          {!empty(data) && !empty(data.exam)
                                            ? data.exam
                                            : 0}
                                        </td>
                                        <td>
                                          {!empty(data) && !empty(data.total)
                                            ? data.total
                                            : 0}
                                        </td>
                                        <td>
                                          {!empty(data) && !empty(data.grade)
                                            ? data.grade
                                            : ""}
                                        </td>
                                        <td>
                                            {classData?.isStream === true ? data?.streamPosition || 'N/A': data?.position || 'N/A'}
                                        </td>
                                        <td>
                                          {!empty(data) && !empty(data.remark)
                                            ? toUpper(data.remark)
                                            : "---"}
                                        </td>
                                        <td>
                                          {!empty(data) &&
                                          !isUndefined(data.classMinValue)
                                            ? data.classMinValue
                                            : "---"}
                                        </td>
                                        <td>
                                          {!empty(data) &&
                                          !isUndefined(data.classMaxValue)
                                            ? data.classMaxValue
                                            : "---"}
                                        </td>
                                        <td>
                                          {!empty(data) &&
                                          !isUndefined(data.classAverage)
                                            ? data.classAverage
                                            : "---"}
                                        </td>
                                      </tr>
                                    );
                                  })
                                ) : (
                                  <tr></tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                          <div className="psycho_area">
                            {!empty(activeDetails) &&
                            !empty(activeDetails.psychomotor) ? (
                              <div className="pyschomotor_box">
                                <table className="psycho-table">
                                  <thead>
                                    <tr>
                                      <th>Psychomotor</th>
                                      <th>Rating</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {isObject(activeDetails.psychomotor) &&
                                      Object.entries(
                                        activeDetails.psychomotor,
                                      ).map(([key, value]) => (
                                        <tr key={key}>
                                          <td>
                                            {!empty(key)
                                              ? toNormalCase(key)
                                              : ""}
                                          </td>
                                          <td>{!empty(value) ? value : ""}</td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : null}
                            {!empty(activeDetails) &&
                            !empty(activeDetails.affectiveDomain) ? (
                              <div className="affective_domain_box">
                                <table className="psycho-table">
                                  <thead>
                                    <tr>
                                      <th>Affective Domain</th>
                                      <th>Rating</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {isObject(activeDetails.affectiveDomain) &&
                                      Object.entries(
                                        activeDetails.affectiveDomain,
                                      ).map(([key, value]) => (
                                        <tr key={key}>
                                          <td>
                                            {!empty(key)
                                              ? toNormalCase(key)
                                              : ""}
                                          </td>
                                          <td>{!empty(value) ? value : ""}</td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : null}
                            {!empty(activeDetails) &&
                            (!empty(activeDetails.affectiveDomain) ||
                              !empty(activeDetails.psychomotor)) &&
                            !empty(ratingIndices) &&
                            isArray(ratingIndices) ? (
                              <div className="mt-10 rating_indicators">
                                <strong>Rating Indicators</strong>
                                <ul style={{ listStyle: "none" }}>
                                  {ratingIndices.map((item, index) => {
                                    const value =
                                      !empty(item) && !empty(item.value)
                                        ? item.value
                                        : "";
                                    const description =
                                      !empty(item) && !empty(item.description)
                                        ? item.description
                                        : "";
                                    return (
                                      <li className="mt-5" key={index}>
                                        <strong>{value}</strong> - {description}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            ) : null}
                            {!empty(activeDetails) &&
                            (!empty(activeDetails.affectiveDomain) ||
                              !empty(activeDetails.psychomotor)) &&
                            !empty(ratingIndices) &&
                            isArray(ratingIndices) ? (
                              <div
                                style={{
                                  width: 400,
                                  height: 150,
                                  display: "flex",
                                  justifyContent: "center",
                                  marginBottom: 10,
                                }}
                              >
                                <Barchart data={chartData} />
                              </div>
                            ) : (
                              <div
                                style={{
                                  width: 500,
                                  height: 220,
                                  display: "flex",
                                  justifyContent: "center",
                                  marginBottom: 10,
                                }}
                              >
                                <Barchart data={chartData} />
                              </div>
                            )}
                          </div>

                          <div className="report-summary-sheet">
                            <table className="report-summary-table">
                              <tbody>
                                <tr>
                                  <td>
                                    <strong>Remarks from teacher</strong>
                                  </td>
                                  <td colSpan={3}>
                                    {!empty(studentReport) &&
                                    !empty(studentReport.comment)
                                      ? studentReport.comment
                                      : !empty(studentReport) &&
                                          !empty(studentReport.teachersComment)
                                        ? studentReport.teachersComment
                                        : "---"}
                                  </td>
                                </tr>
                                <tr>
                                  <td><strong>Class Teacher</strong></td>
                                  <td colSpan={3}>{classTeacherName}</td>
                                </tr>
                                <tr>
                                  <td><strong>Director/Proprietor Remark</strong></td>
                                  <td colSpan={3}>
                                    {!empty(studentReport) &&
                                    !empty(studentReport.principalComments)
                                      ? studentReport.principalComments
                                      : "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td><strong>Director/Proprietor</strong></td>
                                  <td>{user?.ownerName || '---'}</td>
                                  <td colSpan={2}>
                                    {user?.signature ? (
                                      <div className="flex col-g-8">
                                        <span><strong>Sign:</strong></span>
                                        <img
                                          src={user?.signature || ""}
                                          alt="signature"
                                          style={{ width: 40, height: "auto" }}
                                        />
                                      </div>
                                    ) : (
                                      <div>
                                        <strong>Sign:</strong> <em>Management</em>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                                <tr>
                                  <td><strong>Next Term Begins</strong></td>
                                  <td>
                                    {user?.openingDate || "---"}
                                  </td>
                                  <td><strong>Closing Date:</strong> </td>
                                  <td>
                                    {!empty(user) && !empty(user.closingDate)
                                      ? user.closingDate
                                      : "---"}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : !empty(activeDetails) ? (
                      <div className="mt-50 assessment_not_paid">
                        <FaExclamationTriangle color="red" size={25} />
                        <p className="mt-10 fs-18 mb-5">
                          <strong>Result Sheet Unavailable</strong>
                        </p>
                        <p>This student has an outstanding portal levy</p>
                        <div>
                          <ButtonIcon
                            height={45}
                            color={colors.primary}
                            backgroundColor="transparent"
                            width={220}
                            fontWeight="bold"
                            borderColor="transparent"
                            buttonText="Activate Student"
                            onClick={() => setActivationModalOpen(true)}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-center w-100pc">
                        <EmptyListResponse message="There is no result available for this student this term." />
                      </div>
                    )}
                  </>
                ) : (
                  <ItemLoader />
                )}
              </div>
            </div>
            {hasPaid && !isLoading && (
              <div className="flex flex-center mt-20">
                <ButtonIcon
                  height={45}
                  marginTop={2}
                  color={colors.primary}
                  backgroundColor={colors.white}
                  width={180}
                  borderColor={colors.primary}
                  buttonText={
                    <div>
                      <FaPrint /> Print Result
                    </div>
                  }
                  type="submit"
                  borderHover={false}
                  onClick={() => printReport()}
                />
              </div>
            )}
          </div>
        </main>
        {isActionLoading && <FullPageLoader visible={isActionLoading} />}
      </AppWrapper>

      <Dialog
        visible={activationModalOpen}
        style={{ width: "32rem", zIndex: 999999 }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={activateStudentDialogFooter}
        onHide={hideActivationModal}
      >
        <div
          className="confirmation-content"
          style={{ marginTop: 20, display: "flex", alignItems: "center" }}
        >
          <i
            className="pi pi-exclamation-triangle mr-3"
            style={{ fontSize: "2rem", marginRight: 15, color: "#e65061" }}
          />
          {
            <>
              <p>
                Are you sure you want to <strong>activate</strong> this student?{" "}
                <br />
                <br />
                By clicking "Yes", you are confirming that{" "}
                <strong>
                  {!empty(selectedStudentData) &&
                  !empty(selectedStudentData.fullName)
                    ? selectedStudentData.fullName
                    : ""}
                </strong>{" "}
                made portal payment for this term.
                <br />
                <br />
                <em style={{ color: colors.danger }}>
                  <strong>Note:</strong> This action cannot be undone.
                </em>
              </p>
            </>
          }
        </div>
      </Dialog>
      <Toast ref={toastTR} style={{ zIndex: 99999 }} position="bottom-left" />
    </>
  );
};

export default DisplayReportSheet;
